import React, { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DEFAULT_NS } from 'appConstants/translationNamespaces';
import Loader from 'shared-components/Loader';
import { comoLinkActions } from 'store/comoLink/actions';
import { consumeEntryIntent } from 'store/comoLink/entryIntent';
import { MerchantLinkState } from 'store/comoLink/types';
import { AppState } from 'store/rootReducer';
import { ComoHandOff } from './ComoHandOff';
import { decideGate, pickLinked } from './decision';

interface Props {
  programId: string;
  path: string;
  pageId: string;
  children: ReactNode;
}

let entryCounter = 0;
const createEntryId = (): string => {
  entryCounter += 1;
  return `${Date.now().toString(36)}-${entryCounter}`;
};

const Waiting = (): ReactElement => {
  const { t } = useTranslation(DEFAULT_NS);

  return (
    <div style={{ position: 'relative', height: '60vh' }}>
      <Loader loading>
        <span>{t('comoHandOffLoading')}</span>
      </Loader>
    </div>
  );
};

export const ComoProgramGate = ({ programId, path, pageId, children }: Props): ReactElement => {
  const dispatch = useDispatch();
  // One entry per mount: leaving and re-entering the program starts a new entry.
  const entry = useRef<{ id: string; kind: 'dashboard-page' | 'navigation' } | null>(null);
  if (!entry.current) {
    entry.current = {
      id: createEntryId(),
      kind: consumeEntryIntent(path, Date.now()) ? 'dashboard-page' : 'navigation'
    };
  }
  const entryId = entry.current.id;

  const selectionReady = useSelector((state: AppState) => !!state.settings.selectionReady);
  const locations = useSelector((state: AppState) => state.settings.locations);
  const merchantPages = useSelector((state: AppState) => state.settings.merchantPages);
  const program = useSelector((state: AppState) => state.comoLink[programId]);

  const attempted = useRef(false);

  useEffect(() => {
    dispatch(comoLinkActions.fetchState.request({ programId, entryId }));
    // A response arriving after the user left the program must not navigate.
    return () => {
      dispatch(comoLinkActions.handoffCancelled({ programId, entryId }));
    };
  }, [programId]);

  const available = (locations || []).filter(location =>
    ((merchantPages || {})[location.merchant_sequence_key] || []).includes(pageId)
  );
  const current = program && program.entryId === entryId ? program : undefined;
  const linked = available
    .map(location =>
      current?.merchants.find(merchant => merchant.merchantSequenceKey === location.merchant_sequence_key)
    )
    .filter((item): item is MerchantLinkState => !!item && item.target === 'como');
  // Product decision: with several COMO businesses the user is never asked to pick; the best link is used.
  const best = pickLinked(linked);
  const target = best?.status === 'completed' ? best : undefined;
  const decision = decideGate({
    ready: selectionReady && !!current && !current.loading,
    stateError: !!current?.stateError,
    linkStatus: best?.status ?? null,
    entry: entry.current.kind
  });

  const requestHandOff = (merchantSequenceKey: string): void => {
    attempted.current = true;
    dispatch(comoLinkActions.requestMagicLink({ programId, entryId, merchantSequenceKey }));
  };

  useEffect(() => {
    if (decision === 'auto' && target && !attempted.current) {
      requestHandOff(target.merchantSequenceKey);
    }
  }, [decision]);

  // A page restored from the back/forward cache keeps the in-flight state of a hand-off that already left.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) dispatch(comoLinkActions.handoffCancelled({ programId, entryId }));
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [programId]);

  const openRequests = current?.openRequests ?? 0;
  const seenOpenRequests = useRef(0);
  useEffect(() => {
    if (openRequests === seenOpenRequests.current) return;
    seenOpenRequests.current = openRequests;
    const coolingDown = !!current?.retryAt && current.retryAt > Date.now();
    if ((decision === 'confirm' || decision === 'auto') && target && !current?.navigatingMsk && !coolingDown) {
      requestHandOff(target.merchantSequenceKey);
    }
  }, [openRequests]);

  const retryState = (): void => {
    dispatch(comoLinkActions.retryState({ programId, entryId }));
  };

  if (decision === 'legacy') return <>{children}</>;
  if (decision === 'loading' || current?.navigatingMsk) return <Waiting />;
  if (decision === 'auto' && !attempted.current) return <Waiting />;
  if (decision === 'error') {
    return <ComoHandOff error="STATE_ERROR" retryAt={null} onOpen={retryState} titleKey="comoStateErrorTitle" />;
  }
  if (decision === 'blocked') {
    // Only a pending link can change by itself; a failed one needs support.
    return best?.status === 'pending' ? (
      <ComoHandOff error="LINK_PENDING" retryAt={null} onOpen={retryState} actionKey="comoLinkCheckAgain" />
    ) : (
      <ComoHandOff error="LINK_FAILED" retryAt={null} />
    );
  }

  return (
    <ComoHandOff
      error={current?.handoffError || null}
      retryAt={current?.retryAt ?? null}
      onOpen={() => target && requestHandOff(target.merchantSequenceKey)}
    />
  );
};
